import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { chatData } from "../data/chatFlow";
import {
  supabase,
  isSupabaseConfigured,
  syncRealtimeAuth,
} from "../lib/supabaseClient";
import { labelFromUser } from "../utils/chatLabels";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";

import { ChatHeader } from "./chatbot/ChatHeader";
import { GuideTab } from "./chatbot/GuideTab";
import { LiveChatTab } from "./chatbot/LiveChatTab";
import { FloatingChatButton } from "./chatbot/FloatingChatButton";

const LABELS = {
  en: {
    title: "Connected",
    titleAccent: "Support",
    guideTab: "Guide",
    liveTab: "Live chat",
    placeholder: "Type a message…",
    send: "Send",
    welcome: "Hi! How can we help you today?",
    connecting: "Connecting…",
    live: "Live",
    offline: "Chat unavailable",
    offlineHint:
      "Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to your .env file.",
    newChat: "New chat",
    liveGateTitle: "Sign up to use live chat",
    liveGateBody:
      "Create a free account (email + password) to message our team in real time.",
    email: "Email",
    password: "Password",
    displayName: "Display name",
    signIn: "Sign in",
    signUp: "Create account",
    haveAccount: "Already have an account?",
    needAccount: "Need an account?",
    signOutLive: "Sign out",
    clearHistory: "Clear my messages",
    strategicChoices: "Strategic Choices",
  },
  mm: {
    title: "Connected",
    titleAccent: "Support",
    guideTab: "လမ်းညွှန်",
    liveTab: "တိုက်ရိုက် ချတ်",
    placeholder: "မက်ဆေ့ချ် ရိုက်ပါ…",
    send: "ပို့မည်",
    welcome: "မင်္ဂလာပါ။ ဘယ်လို ကူညီရမလဲ?",
    connecting: "ချိတ်ဆက်နေသည်…",
    live: "တိုက်ရိုက်",
    offline: "ချတ်မရနိုင်ပါ",
    offlineHint: ".env တွင် Supabase သော့များထည့်ပါ။",
    newChat: "ချတ်အသစ်",
    liveGateTitle: "တိုက်ရိုက်ချတ်အတွက် စာရင်းသွင်းပါ",
    liveGateBody:
      "အီးမေးလ် နှင့် စကားဝှက်ဖြင့် အကောင့်ဖွင့်ပြီး တိုက်ရိုက် စာပို့နိုင်ပါသည်။",
    email: "အီးမေးလ်",
    password: "စကားဝှက်",
    displayName: "အမည်",
    signIn: "ဝင်ရန်",
    signUp: "အကောင့်ဖွင့်ရန်",
    haveAccount: "အကောင့်ရှိပြီးသား?",
    needAccount: "အကောင့်မရှိဘူးလား?",
    signOutLive: "ထွက်ရန်",
    clearHistory: "မက်ဆေ့များ ဖျက်ရန်",
    strategicChoices: "ရွေးချယ်စရာများ",
  },
};

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const [tab, setTab] = useState("guide");
  const t = LABELS[lang] || LABELS.en;

  const {
    isAuthenticated,
    signUpWithEmail,
    signInWithEmail,
    signInWithGoogle,
    signOut,
    user,
  } = useAuth();

  const [authMode, setAuthMode] = useState("signin");
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    displayName: "",
  });

  const [nodeKey, setNodeKey] = useState("start");
  const [flowHistory, setFlowHistory] = useState(() => [
    { sender: "bot", text: chatData.en?.start?.message || "" },
  ]);
  const [isTyping, setIsTyping] = useState(false);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [liveReady, setLiveReady] = useState(false);
  const [initError, setInitError] = useState(null);

  const scrollRef = useRef(null);
  const channelRef = useRef(null);
  const liveUidRef = useRef(null);
  const lastSeenMsgIdRef = useRef(null);

  const canUseLive = Boolean(user) && user.is_anonymous !== true;
  const showOffline = !isSupabaseConfigured;

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [flowHistory, isTyping, messages, tab, scrollToBottom]);

  useEffect(() => {
    if (tab === "live") {
      // mark messages as seen when user opens the live tab
      const last = messages[messages.length - 1];
      if (last && last.id) lastSeenMsgIdRef.current = last.id;
    }
  }, [tab, messages]);

  const unreadCount = React.useMemo(() => {
    if (!messages || messages.length === 0) return 0;
    if (tab === "live") return 0;
    const lastSeenId = lastSeenMsgIdRef.current;
    if (!lastSeenId) return messages.filter((m) => m.sender === "agent").length;
    const idx = messages.findIndex((m) => m.id === lastSeenId);
    const after = idx === -1 ? messages : messages.slice(idx + 1);
    return after.filter((m) => m.sender === "agent").length;
  }, [messages, tab]);

  useEffect(() => {
    setNodeKey("start");
    setFlowHistory([
      { sender: "bot", text: chatData[lang]?.start?.message || "" },
    ]);
  }, [lang]);

  const goHomeFlow = () => {
    setNodeKey("start");
    setFlowHistory([
      { sender: "bot", text: chatData[lang]?.start?.message || "" },
    ]);
  };

  const handleOptionClick = (label, nextKey) => {
    setFlowHistory((prev) => [...prev, { sender: "user", text: label }]);
    setIsTyping(true);
    setTimeout(() => {
      const nextNode = chatData[lang][nextKey];
      setFlowHistory((prev) => [
        ...prev,
        { sender: "bot", text: nextNode.message },
      ]);
      setNodeKey(nextKey);
      setIsTyping(false);
    }, 800);
  };

  const mergeMessage = useCallback((row) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === row.id)) return prev;
      const next = [...prev, row];
      next.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
      return next;
    });
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase || !canUseLive) {
      if (channelRef.current) {
        supabase?.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      setLiveReady(false);
      liveUidRef.current = null;
      return;
    }

    let cancelled = false;

    async function initLive() {
      setInitError(null);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || user.is_anonymous) {
          setLiveReady(false);
          return;
        }
        const uid = user.id;
        liveUidRef.current = uid;

        const { data: rows, error: fetchErr } = await supabase
          .from("chat_messages")
          .select(
            "id, conversation_id, sender, content, created_at, author_label, sender_id",
          )
          .eq("conversation_id", uid)
          .order("created_at", { ascending: true });

        if (fetchErr) throw fetchErr;
        if (!cancelled) setMessages(rows || []);

        await syncRealtimeAuth(supabase);
        if (cancelled) return;

        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }

        const channel = supabase
          .channel(`chat:${uid}`)
          .on(
            "postgres_changes",
            {
              event: "INSERT",
              schema: "public",
              table: "chat_messages",
            },
            (payload) => {
              const row = payload.new;
              if (row && row.conversation_id === uid && row.id) {
                mergeMessage(row);
              }
            },
          )
          .subscribe();

        channelRef.current = channel;
        if (!cancelled) setLiveReady(true);
      } catch (e) {
        if (!cancelled) {
          setInitError(e?.message || "init_failed");
          setLiveReady(false);
        }
      }
    }

    initLive();

    return () => {
      cancelled = true;
      if (channelRef.current && supabase) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [canUseLive, mergeMessage, user?.id]);

  useEffect(() => {
    if (
      !supabase ||
      !isSupabaseConfigured ||
      !isOpen ||
      !liveReady ||
      !canUseLive ||
      tab !== "live"
    ) {
      return;
    }
    const id = liveUidRef.current;
    if (!id) return;

    const refresh = async () => {
      const { data, error } = await supabase
        .from("chat_messages")
        .select(
          "id, conversation_id, sender, content, created_at, author_label, sender_id",
        )
        .eq("conversation_id", id)
        .order("created_at", { ascending: true });
      if (!error && data) setMessages(data);
    };

    refresh();
    const interval = setInterval(refresh, 2000);
    return () => clearInterval(interval);
  }, [isOpen, liveReady, canUseLive, tab]);

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthBusy(true);
    setAuthError("");

    try {
      if (authMode === "signup") {
        if (!formData.displayName) {
          setAuthError("Display name is required");
          return;
        }
        await signUpWithEmail(
          formData.email,
          formData.password,
          formData.displayName,
        );
        setAuthMode("signin");
        setAuthError("");
        setFormData({ ...formData, password: "" });
      } else {
        await signInWithEmail(formData.email, formData.password);
      }
    } catch (error) {
      if (
        error.message?.includes("rate limit") ||
        error.message?.includes("exceeded")
      ) {
        setAuthError(
          "Too many signup attempts. Please wait a few minutes and try again.",
        );
      } else {
        setAuthError(error.message || "Authentication failed");
      }
    } finally {
      setAuthBusy(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthBusy(true);
    setAuthError("");
    try {
      await signInWithGoogle();
    } catch (error) {
      setAuthError(error.message || "Google sign-in failed");
    } finally {
      setAuthBusy(false);
    }
  };

  const handleSignOut = async () => {
    setAuthBusy(true);
    try {
      await signOut();
      setFormData({ email: "", password: "", displayName: "" });
      setAuthError("");
    } catch (error) {
      setAuthError(error.message || "Sign-out failed");
    } finally {
      setAuthBusy(false);
    }
  };

  const handleSendLive = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || !supabase || sending || !liveReady || !canUseLive) return;

    setSending(true);
    setInput("");
    try {
      if (!user || user.is_anonymous) throw new Error("Sign in required");

      const { data, error } = await supabase
        .from("chat_messages")
        .insert({
          conversation_id: user.id,
          sender: "visitor",
          content: text,
          sender_id: user.id,
          author_label: labelFromUser(user),
        })
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setInitError(null);
        mergeMessage(data);
      }
    } catch (err) {
      setInput(text);
      setInitError(err?.message || "send_failed");
    } finally {
      setSending(false);
    }
  };

  const signOutLive = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setMessages([]);
    setLiveReady(false);
  };

  const clearLiveHistory = async () => {
    if (!supabase || !canUseLive) return;

    const uid = liveUidRef.current;
    if (!uid) return;

    toast(
      (t) => (
        <div className="w-[340px] rounded-[1.8rem] border border-white/10 bg-[#0B0F19] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5">
          <div className="mb-4">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-white italic">
              Delete Chat <span className="text-[#00BCF0]">History</span>
            </p>

            <div className="w-10 h-[2px] bg-[#EAB308] mt-2 mb-3 rounded-full" />

            <p className="text-xs leading-relaxed text-slate-400">
              Permanently remove all live chat messages from this conversation.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 text-[10px] font-black uppercase tracking-[0.18em] text-slate-300 hover:bg-white/10 transition-all">
              Cancel
            </button>

            <button
              onClick={async () => {
                toast.dismiss(t.id);

                try {
                  const { error } = await supabase
                    .from("chat_messages")
                    .delete()
                    .eq("conversation_id", uid);

                  if (error) throw error;

                  setMessages([]);
                  setInitError(null);

                  toast.success("History Cleared", {
                    style: {
                      background: "#0B0F19",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: "16px",
                    },
                  });
                } catch (error) {
                  setInitError(error.message);

                  toast.error("Delete Failed", {
                    style: {
                      background: "#0B0F19",
                      color: "#fff",
                      border: "1px solid rgba(239,68,68,0.25)",
                      borderRadius: "16px",
                    },
                  });
                }
              }}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#FF3B30] to-[#FF5A36] text-[10px] font-black uppercase tracking-[0.18em] text-white hover:scale-105 transition-all shadow-[0_8px_20px_rgba(255,59,48,0.25)]">
              Delete
            </button>
          </div>
        </div>
      ),
      {
        duration: 7000,
        position: "top-center",
        style: {
          background: "rgba(0, 0, 0, 0.25)",
          boxShadow: "none",
          padding: 0,
          margin: 0,
          backdropFilter: "blur(6px)",
          borderRadius: "12px",
        },
      },
    );
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col items-end font-sans selection:bg-[#EAB308] selection:text-black">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #00BCF0; }
      `}</style>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 40, filter: "blur(10px)" }}
            animate={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.9, y: 40, filter: "blur(10px)" }}
            className="relative w-[calc(100vw-2rem)] sm:w-[380px] md:w-[400px] min-h-[50vh] h-[70vh] sm:h-[600px] max-h-[85vh] bg-[#161B22]/20 backdrop-blur-xl border border-white/10 rounded-[1.5rem] sm:rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col mb-4 md:mb-6 overflow-hidden ring-1 ring-white/5">
            <div className="absolute inset-0 bg-[#007BFF]/5 blur-[80px] md:blur-[100px] -z-10" />

            <ChatHeader
              t={t}
              lang={lang}
              setLang={setLang}
              tab={tab}
              unreadCount={unreadCount}
              setTab={setTab}
              canUseLive={canUseLive}
              onClose={() => setIsOpen(false)}
              onNewChat={goHomeFlow}
              onClearHistory={clearLiveHistory}
              onSignOut={signOutLive}
            />

            <div
              ref={scrollRef}
              className="flex-grow min-h-0 overflow-y-auto space-y-3 md:space-y-5 custom-scrollbar p-3 md:p-5">
              {tab === "guide" && (
                <GuideTab
                  t={t}
                  flowHistory={flowHistory}
                  isTyping={isTyping}
                  nodeKey={nodeKey}
                  lang={lang}
                  onOptionClick={handleOptionClick}
                />
              )}

              {tab === "live" && (
                <LiveChatTab
                  t={t}
                  showOffline={showOffline}
                  canUseLive={canUseLive}
                  authMode={authMode}
                  setAuthMode={setAuthMode}
                  formData={formData}
                  setFormData={setFormData}
                  authError={authError}
                  authBusy={authBusy}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  onAuthSubmit={handleAuthSubmit}
                  onGoogleSignIn={handleGoogleSignIn}
                  initError={initError}
                  liveReady={liveReady}
                  messages={messages}
                  input={input}
                  setInput={setInput}
                  onSendMessage={handleSendLive}
                  sending={sending}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <FloatingChatButton isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} />
    </div>
  );
}
